import React from 'react';
import { motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Blocks, ShoppingCart, Store, CheckCircle2, ArrowRight, Cloud, Smartphone, Database } from "lucide-react";

const Integrations = () => {
  const integrations = [
    {
      name: "Shopify",
      category: "E-Commerce",
      icon: ShoppingCart,
      color: "text-emerald-400",
      bg: "bg-emerald-400/10",
      description: "Seamlessly accept payments on your Shopify store with native checkout support."
    },
    {
      name: "WooCommerce",
      category: "E-Commerce",
      icon: Store,
      color: "text-violet-400",
      bg: "bg-violet-400/10",
      description: "A free open-source plugin to bring Luxkgate to your WordPress site."
    },
    {
      name: "Magento",
      category: "Enterprise E-Commerce",
      icon: Blocks,
      color: "text-orange-400",
      bg: "bg-orange-400/10",
      description: "Direct integration for high-volume enterprise stores running Adobe Commerce."
    },
    {
      name: "Accounting Sync",
      category: "Finance",
      icon: Database,
      color: "text-blue-400",
      bg: "bg-blue-400/10",
      description: "Automatically reconcile your payment data with QuickBooks or Xero."
    },
    {
      name: "Mobile SDKs",
      category: "Development",
      icon: Smartphone,
      color: "text-rose-400",
      bg: "bg-rose-400/10",
      description: "Native iOS and Android drop-in UI components for building frictionless mobile checkout."
    },
    {
      name: "Zapier",
      category: "Automation",
      icon: Cloud,
      color: "text-amber-400",
      bg: "bg-amber-400/10",
      description: "Connect your payment events to thousands of other apps using Zapier workflows."
    }
  ];

  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <Blocks className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Plug & Play Ecosystem</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Connect with everything <br />
            <span className="gradient-text">you already use</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Launch faster by using our pre-built plugins and partner integrations. We support the world's most popular platforms right out of the box.
          </p>
        </motion.div>

        {/* Integration Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {integrations.map((app, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              <Card className="bg-slate-900/50 border-slate-800/50 h-full hover:border-slate-700 transition-colors group flex flex-col">
                <CardContent className="p-8 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-12 h-12 rounded-xl ${app.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <app.icon className={`h-6 w-6 ${app.color}`} />
                    </div>
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-slate-800 text-slate-300">
                      {app.category}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{app.name}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-8 flex-1">
                    {app.description}
                  </p>

                  <div className="mt-auto border-t border-slate-800/50 pt-4 flex items-center justify-between">
                    <span className="flex items-center text-sm text-emerald-400">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      Supported
                    </span>
                    <Button variant="ghost" className="text-slate-400 hover:text-white group-hover:text-blue-400 h-8 px-3">
                      View Guide <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Custom Build CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="bg-gradient-to-r from-blue-900/20 to-violet-900/20 border-blue-500/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-32 bg-blue-500/10 blur-[100px] rounded-full mix-blend-screen" />
            <CardContent className="p-12 md:p-16 relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">Don't see your platform?</h2>
                <p className="text-slate-300 max-w-xl text-lg">
                  Use our comprehensive API and webhooks to build a custom integration exactly tailored to your business needs.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full md:w-auto">
                <Button className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-6 text-base font-semibold" onClick={() => window.location.href = '/api-docs'}>
                  Read API Docs
                </Button>
                <Button variant="outline" className="bg-transparent border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-500 px-8 py-6 text-base">
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

      </div>
    </div>
  );
};

export default Integrations;
